import { Box } from '@mui/material';
import { neutral } from '@ahora-patagonia/design-tokens';
import type { MediaVariantRatio, PublicImage } from '@ahora-patagonia/contracts';
import { srcsetDe } from './srcset.js';

export interface ImagenNotaProps {
  imagen: PublicImage | null;
  /** Qué **recorte guardado** se pide (ver `MEDIA_VARIANT_RATIOS`). */
  ratio: MediaVariantRatio;
  /**
   * Alto de la caja, cuando no es el del recorte pedido. Sirve para la maqueta B de la portada, que
   * quiere un 21:9 en escritorio: ese recorte no existe en el bucket, así que se pide el 16:9 —la
   * fuente más ancha— y la caja lo recorta un poco más arriba y abajo. La alternativa era una
   * variante nueva en el worker para una sola celda del sitio.
   *
   * Se declara **por breakpoint**, como cualquier `sx`: nada se mide, el corte lo hace el CSS.
   */
  caja?: string | Record<string, string>;
  /** `sizes` de la plantilla (ver `SIZES`). */
  sizes?: string;
  /** Solo la posición 1 de la portada: `fetchpriority="high"` + carga inmediata. */
  prioritaria?: boolean;
}

const ASPECT: Record<MediaVariantRatio, string> = {
  '16:9': '16 / 9',
  '4:3': '4 / 3',
  '3:4': '3 / 4',
};

/**
 * Una foto de nota en una lista o portada. La caja reserva el alto con `aspect-ratio` CSS
 * (nada se mueve al cargar) y el recorte se encuadra al **tercio superior**
 * (`object-position: center 30%`): en la foto vertical de celular, ahí está la cara.
 *
 * `loading="lazy"` por defecto — todo lo que está bajo el pliegue se difiere; la única
 * excepción es la posición 1 de la portada (`prioritaria`).
 *
 * El alt viene garantizado por el producto (portada + alt son requisito para publicar):
 * acá no hay alt de emergencia.
 */
export function ImagenNota({ imagen, ratio, caja, sizes, prioritaria = false }: ImagenNotaProps) {
  const fuente = srcsetDe(imagen, ratio);

  return (
    <Box
      sx={{
        aspectRatio: caja ?? ASPECT[ratio],
        overflow: 'hidden',
        backgroundColor: neutral[300],
        width: '100%',
      }}
    >
      {fuente && (
        <img
          src={fuente.src}
          {...(fuente.srcSet ? { srcSet: fuente.srcSet } : {})}
          {...(sizes ? { sizes } : {})}
          alt={imagen?.alt ?? ''}
          loading={prioritaria ? 'eager' : 'lazy'}
          // React 18 no tipa fetchpriority; en minúscula pasa como atributo HTML crudo.
          {...({ fetchpriority: prioritaria ? 'high' : undefined } as Record<string, unknown>)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: 'center 30%',
            display: 'block',
          }}
        />
      )}
    </Box>
  );
}
